/** @format */

import React, { FunctionComponent } from 'react';
import SimCardInterface from '../../../interfaces/platform/SimCardInterface';
import GridItem from '../../../components/Grid/GridItem';
import GridContainer from '../../../components/Grid/GridContainer';
import Typography from '@material-ui/core/Typography';
import I18n from '../../../components/Translate/I18n';
import { iccidOperatorStatus, PlkStatusMap } from '../../../helpers/iccidStatus';
import { SimCardOperationHelper } from '../../../helpers/SimCardOperationHelper';
import { plkTariffIntToName, plkTariffNameToName } from '../../../helpers/PlkTariffsMapper';
import SimCardSynchronize from './SimCardSynchronize';
import Divider from '@material-ui/core/Divider';
import { DateHelper } from '../../../helpers/DateHelper';
import { OperatorMvneTypeMap } from '../../../modules/lib/hooks/useOperatorType';

export type ProductViewDetailType = {
    product: SimCardInterface;
};

export const ProductViewDetail: FunctionComponent<ProductViewDetailType> = ({ product }) => {
    let msisdn = product.msisdn;
    if (product.deleted) {
        msisdn = product.lastMsisdn ?? '';
    }

    return (
        <GridContainer>
            <GridItem xs={12} md={6} lg={4}>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.msisdn" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {msisdn}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.iccid" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.iccid}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.imsi" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.imsi}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.iccid_type" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.iccidType}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.pin1" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.pin1}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.puk1" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.puk1}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.pin2" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.pin2}
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="pools:pool.puk2" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.puk2}
                        </Typography>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem xs={12} md={6} lg={4}>
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.sim_card_status_mvno" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans={iccidOperatorStatus(product.mvnoStatus)} />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.status_local" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans={SimCardOperationHelper.getSimCardStatus(product)} />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.financial_settlement" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.tariffName}
                        </Typography>
                    </GridItem>

                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:v.currency_balance" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {product.mnoCurrencyBalance && product.mnoCurrencyBalance.toFixed(2)} PLN
                        </Typography>
                    </GridItem>
                    {product.spnValue && (
                        <>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:spn.spn" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {Buffer.from(product.spnValue, 'base64').toString('utf-8')}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:spn.spn_set_date" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.spnChangeDate)}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </GridItem>
            <GridItem xs={12} md={6} lg={4}>
                {!product.deleted && <SimCardSynchronize simCard={product} />}
                <Divider flexItem={true} light={true} />
                <GridContainer>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            <I18n trans="simcard:list.createdAt" />
                        </Typography>
                    </GridItem>
                    <GridItem xs={6}>
                        <Typography variant="subtitle2" gutterBottom>
                            {DateHelper.format(product.createdAt)}
                        </Typography>
                    </GridItem>
                    {product.mvnoStatus > PlkStatusMap.PREACTIVATED && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:list.activation_date" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.activateDate)}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    {product.operatorType !== OperatorMvneTypeMap.OPL && product.active && !product.deleted && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:v.incoming_call_expiry_date" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.incomingCallExpiryDate)}
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:v.outgoing_call_expiry_date" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.outgoingCallExpiryDate)}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    {product.expiryDate && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:list.activated_to" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.expiryDate)}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    {product.removePeriod && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:list.planned_deleted_at" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.removePeriod)}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                    {product.deleted && (
                        <React.Fragment>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <I18n trans="simcard:list.deleted_at" />
                                </Typography>
                            </GridItem>
                            <GridItem xs={6}>
                                <Typography variant="subtitle2" gutterBottom>
                                    {DateHelper.format(product.deletedDate)}
                                </Typography>
                            </GridItem>
                        </React.Fragment>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};
